import {useDraggable} from '@dnd-kit/core';
import {CSS} from '@dnd-kit/utilities';
import type {CSSProperties, PointerEventHandler} from 'react';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {noop} from '@Utils/general.util';
import type {DragDirection, ItemData, ResizeEndEvent, ResizeMoveEvent, ResizeStartEvent, UseItemProps} from '../types';
import useTimelineContext from './useTimelineContext';

const getDragDirection = (mouseX: number, clientRect: DOMRect, direction: CanvasDirection, resizeHandleWidth: number): DragDirection | null => {
  const startSide = direction === 'rtl' ? 'right' : 'left';
  const endSide = direction === 'rtl' ? 'left' : 'right';

  if (Math.abs(mouseX - clientRect[startSide]) <= resizeHandleWidth / 2) {
    return 'start';
  }

  if (Math.abs(mouseX - clientRect[endSide]) <= resizeHandleWidth / 2) {
    return 'end';
  }

  return null;
};

export default function useItem({disableDragOnly = false, disableResizeOnly = false, ...props}: UseItemProps) {
  const dataRef = useRef<ItemData>({} as ItemData);
  const dragStartX = useRef<number>(undefined);
  const [dragDirection, setDragDirection] = useState<DragDirection | null>();

  const {range, overlayed, onResizeEnd, onResizeMove, onResizeStart, direction, resizeHandleWidth, valueToPixels, getSpanFromDragEvent, getSpanFromResizeEvent} =
    useTimelineContext();
  const resizeHandleWidthRecomputed = disableResizeOnly ? 0 : resizeHandleWidth;

  const propsOnResizeEnd = props.onResizeEnd;
  const propsOnResizeStart = props.onResizeStart;
  const propsOnResizeMove = props.onResizeMove;

  const onResizeEndCallback = useCallback(
    (event: ResizeEndEvent) => {
      onResizeEnd(event);
      propsOnResizeEnd?.(event);
    },
    [onResizeEnd, propsOnResizeEnd]
  );

  const onResizeStartCallback = useCallback(
    (event: ResizeStartEvent) => {
      onResizeStart?.(event);
      propsOnResizeStart?.(event);
    },
    [onResizeStart, propsOnResizeStart]
  );

  const onResizeMoveCallback = useCallback(
    (event: ResizeMoveEvent) => {
      onResizeMove?.(event);
      propsOnResizeMove?.(event);
    },
    [onResizeMove, propsOnResizeMove]
  );

  dataRef.current = {
    getSpanFromDragEvent,
    getSpanFromResizeEvent,
    span: props.span,
    ...(props.data || {}),
  };

  const draggableProps = useDraggable({
    id: props.id,
    data: dataRef.current,
    disabled: props.disabled,
  });

  const getCursorValue = (): 'inherit' | 'grabbing' | 'grab' => {
    if (props.disabled) {
      return 'inherit';
    }

    return draggableProps.isDragging ? 'grabbing' : 'grab';
  };

  const [deltaXStart, setDeltaXStart] = useState(valueToPixels(props.span.start - range.start));

  const deltaXEnd = valueToPixels(range.end - props.span.end);

  const [width, setWidth] = useState(valueToPixels(props.span.end - props.span.start));
  const duplicateWidth = useRef<number>(width);
  const duplicateLeft = useRef<number>(deltaXStart);
  const [isResizing, setIsResizing] = useState(false);

  const sideStart = direction === 'rtl' ? 'right' : 'left';

  const sideEnd = direction === 'rtl' ? 'left' : 'right';

  const cursor = getCursorValue();

  const updateValues = (): void => {
    setWidth(valueToPixels(props.span.end - props.span.start));
    duplicateWidth.current = valueToPixels(props.span.end - props.span.start);
    setDeltaXStart(valueToPixels(props.span.start - range.start));
    duplicateLeft.current = valueToPixels(props.span.start - range.start);
  };

  useEffect(() => {
    updateValues();
  }, [valueToPixels]);

  useEffect(() => {
    if (draggableProps.isDragging) {
      return;
    }
    if (draggableProps.node.current) {
      draggableProps.node.current.style.width = `${valueToPixels(props.span.end - props.span.start)}px`;
      draggableProps.node.current.style[sideStart] = `${valueToPixels(props.span.start - range.start)}px`;
    }
    if (!isResizing) {
      updateValues();
    }
  }, [props.span]);

  useLayoutEffect(() => {
    if (!dragDirection) return noop;

    const pointermoveHandler = (event: PointerEvent): void => {
      if (!dragStartX.current || !draggableProps.node.current) return;

      const dragDeltaX = (event.clientX - dragStartX.current) * (direction === 'rtl' ? -1 : 1);
      let differenceMoved = 0;

      if (dragDirection === 'start') {
        differenceMoved = Number(draggableProps.node.current.style[sideStart].replace('px', '')) - duplicateLeft.current;
      } else if (draggableProps.node.current) {
        differenceMoved = Number(draggableProps.node.current.style.width.replace('px', '')) - duplicateWidth.current;
      }

      setIsResizing(true);

      onResizeMoveCallback({
        delta: {
          x: dragDeltaX - differenceMoved,
        },
        direction: dragDirection,
        active: {
          id: props.id,
          data: dataRef,
        },
      });
    };

    window.addEventListener('pointermove', pointermoveHandler);

    return (): void => {
      window.removeEventListener('pointermove', pointermoveHandler);
    };
  }, [sideStart, width, deltaXStart, props.id, dragDirection, direction, draggableProps.node, onResizeMoveCallback]);

  useLayoutEffect(() => {
    if (!dragDirection) return noop;

    const pointerupHandler = (): void => {
      if (!dragStartX.current || !draggableProps.node.current) return;

      const dragDeltaX = 0;

      if (dragDirection === 'start') {
        duplicateLeft.current = Number.parseInt(draggableProps.node.current.style[sideStart].slice(0, -2), 10);
        duplicateWidth.current = Number.parseInt(draggableProps.node.current.style.width.slice(0, -2), 10);
      } else {
        duplicateWidth.current = Number.parseInt(draggableProps.node.current.style.width.slice(0, -2), 10);
      }

      onResizeEndCallback({
        delta: {
          x: dragDeltaX,
        },
        direction: dragDirection,
        active: {
          id: props.id,
          data: dataRef,
        },
      });

      setIsResizing(false);
      setDragDirection(null);
    };

    window.addEventListener('pointerup', pointerupHandler);

    return (): void => {
      window.removeEventListener('pointerup', pointerupHandler);
    };
  }, [sideStart, width, deltaXStart, props.id, dragDirection, draggableProps.node, onResizeEndCallback]);

  const onPointerMove = useCallback<PointerEventHandler>(
    (event) => {
      if (!draggableProps.node.current || props.disabled) return;

      const newDragDirection = getDragDirection(event.clientX, draggableProps.node.current.getBoundingClientRect(), direction, resizeHandleWidthRecomputed);

      if (newDragDirection) {
        draggableProps.node.current.style.cursor = 'col-resize';
      } else {
        draggableProps.node.current.style.cursor = cursor;
      }
    },
    [draggableProps.node, props.disabled, direction, cursor, resizeHandleWidthRecomputed]
  );

  const onPointerUp = useCallback<PointerEventHandler>(() => {
    if (!draggableProps.node.current || props.disabled) return;

    updateValues();
  }, [props.id, props.span, props.disabled, direction, resizeHandleWidthRecomputed, draggableProps.node, draggableProps.listeners]);

  const onPointerDown = useCallback<PointerEventHandler>(
    (event) => {
      if (!draggableProps.node.current || props.disabled) return;

      const newDragDirection = getDragDirection(event.clientX, draggableProps.node.current.getBoundingClientRect(), direction, resizeHandleWidthRecomputed);

      if (newDragDirection) {
        setDragDirection(newDragDirection);
        dragStartX.current = event.clientX;

        onResizeStartCallback({
          active: {
            id: props.id,
            data: dataRef,
          },
          direction: newDragDirection,
        });
      } else if (!disableDragOnly) {
        draggableProps.listeners?.onPointerDown(event);
      }
    },
    [props.id, props.disabled, direction, resizeHandleWidthRecomputed, draggableProps.node, onResizeStartCallback, draggableProps.listeners]
  );

  const paddingStart = direction === 'rtl' ? 'paddingRight' : 'paddingLeft';

  const paddingEnd = direction === 'rtl' ? 'paddingLeft' : 'paddingRight';

  const itemStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    width,
    [sideStart]: deltaXStart,
    [sideEnd]: deltaXEnd,
    cursor,
    height: '100%',
    touchAction: 'none',
    ...(!(draggableProps.isDragging && overlayed) && {
      transform: CSS.Translate.toString(draggableProps.transform),
    }),
  };

  const itemContentStyle: CSSProperties = {
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    alignItems: 'stretch',
    [paddingStart]: Math.max(0, -deltaXStart),
    [paddingEnd]: Math.max(0, -deltaXEnd),
  };

  return {
    itemStyle,
    itemContentStyle,
    ...draggableProps,
    listeners: {
      ...draggableProps.listeners,
      onPointerUp,
      onPointerDown,
      onPointerMove,
    },
  };
}
